<template>
  <div class="has-many cta-list">
    <div class="has-many-title">
      <h2 class="has-many-title__title">Call to Action Buttons</h2>
      <span class="has-many-title__count">{{ items.length }} / {{ max }}</span>
    </div>

    <v-expansion-panels class="panels" accordion v-model="panel" multiple>
      <v-expansion-panel class="form-panel" v-for="(item, idx) in items" :key="item.uuid">
        <v-expansion-panel-header @keyup.space.prevent>
          <div class="has-many__header-row">
            <span class="has-many__title-field has-many__title-label">
              {{ item.text || "New CTA" }}
            </span>
            <div class="has-many__actions">
              <v-btn class="has-many-move-up" icon @click.stop="moveUp(idx)" :disabled="idx == 0">
                <v-icon>$arrow_up</v-icon>
              </v-btn>
              <v-btn
                class="has-many-move-up"
                icon
                @click.stop="moveDown(idx)"
                :disabled="idx == items.length - 1"
              >
                <v-icon>$arrow_down</v-icon>
              </v-btn>
              <v-btn class="has-many-delete" icon @click.stop="remove(idx)">
                <v-icon>$delete</v-icon>
              </v-btn>
            </div>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content eager>
          <div class="has-many-field">
            <FormRow label="Button Text" required>
              <TextField
                placeholder="e.g. Watch Now"
                v-model="item.text"
                :rules="[validation.required]"
              ></TextField>
            </FormRow>
            <FormRow label="Link URL" required>
              <TextField
                placeholder="https://…"
                v-model="item.url"
                :rules="[validation.required, validation.url]"
              ></TextField>
            </FormRow>
            <FormRow label="Open in New Tab">
              <v-checkbox v-model="item.new_tab" hide-details></v-checkbox>
            </FormRow>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-btn color="primary" outlined :disabled="atMax" @click="add">
      + Add CTA
    </v-btn>
  </div>
</template>

<script>
// Styled/interacts like the HasMany data loader schema field
// (src/components/schema/builder/HasMany.vue) — a bounded, reorderable,
// expansion-panel list — but for a fixed CTA shape instead of sub-schemas.
import _ from "lodash";
import { getRanHex } from "@/lib";

export default {
  name: "CtaList",
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    max: {
      type: Number,
      default: 3,
    },
  },
  data() {
    return {
      items: (this.value || []).map((t) => ({ uuid: getRanHex(8), ...t })),
      panel: [],
    };
  },
  computed: {
    atMax() {
      return this.items.length >= this.max;
    },
  },
  methods: {
    add() {
      if (this.atMax) return;
      this.items.push({ uuid: getRanHex(8), text: "", url: "", new_tab: false });
      this.panel.push(this.items.length - 1);
    },
    remove(idx) {
      this.items.splice(idx, 1);
    },
    moveUp(idx) {
      if (idx < 1) return;
      const element = { ...this.items[idx] };
      this.items.splice(idx, 1);
      this.items.splice(idx - 1, 0, element);
    },
    moveDown(idx) {
      if (idx >= this.items.length - 1) return;
      const element = { ...this.items[idx] };
      this.items.splice(idx, 1);
      this.items.splice(idx + 1, 0, element);
    },
  },
  watch: {
    items: {
      deep: true,
      handler() {
        this.$emit(
          "input",
          this.items.map((item) => _.omit(item, "uuid"))
        );
      },
    },
    value: {
      deep: true,
      handler(newVal) {
        const stripped = this.items.map((item) => _.omit(item, "uuid"));
        if (JSON.stringify(newVal || []) !== JSON.stringify(stripped)) {
          this.items = (newVal || []).map((t) => ({ uuid: getRanHex(8), ...t }));
        }
      },
    },
  },
};
</script>

<style scoped lang="scss">
.has-many {
  background: #0000000c;
  margin: 25px 0;
  padding: 15px;

  &::v-deep {
    .v-expansion-panel-header {
      border: none;
    }
  }
}

.has-many-title {
  font-weight: bold;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.has-many-title__count {
  font-weight: 400;
  font-size: 13px;
  color: #8a8a8e;
}

.has-many-field {
  padding: 20px 40px;
  position: relative;
  background: white;
}

.has-many__header-row {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 8px;
  width: 100%;
}

.has-many__title-field {
  flex: 1 1 200px;
  min-width: 0;
}

.has-many__title-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.has-many__actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: auto;
}

.panels {
  margin-bottom: 10px;

  &::v-deep {
    .v-expansion-panel-content__wrap {
      padding: 0;
    }
  }
}

.env-production {
  .has-many-field {
    background: #262729 !important;
  }
}
</style>
